import React from 'react';
import styled from 'styled-components';
import { isNil } from '@peloton/helpers/isNil';
import { defaultTransition } from '@peloton/styles';

type Props = {
  isOpen: boolean;
  recalculateHeightOn?: boolean;
  minimumHeight?: number;
  id?: string;
};

const Body = styled.div<Props & { boxHeight: number }>`
  overflow: hidden;
  ${defaultTransition('height', 350)}
  height: ${props =>
    props.isOpen ? `${props.boxHeight}px` : `${props.minimumHeight}px`};
  & > * {
    height: ${props => (props.isOpen ? 'auto' : '0px')};
  }
`;

const useChildHeight = (
  ref: React.MutableRefObject<HTMLDivElement | null>,
  isOpen: boolean,
  recalculateHeightOn?: boolean,
) => {
  const [height, setHeight] = React.useState(0);

  React.useEffect(() => {
    if (ref.current) {
      let totalHeight = 0;
      for (let i = 0; i < ref.current.children.length; ++i) {
        totalHeight += ref.current.children[i].scrollHeight;
      }
      setHeight(totalHeight);
    }
  }, [ref, isOpen, recalculateHeightOn]);

  return height;
};

/**
 *
 * @param recalculateHeightOn an optional boolean that will force Collapsible
 * to recalculate its height when this value changes. Useful for keeping the Collapsible
 * in sync if you have multi-level drop downs under a single component (See `LanguageOption.tsx`)
 *
 * @param minimumHeight provides the option to have the Collapsible always 'open' some minimum amount
 * (See `Music.tsx`)
 */
export const Collapsible: React.FC<React.PropsWithChildren<Props>> = ({
  isOpen,
  recalculateHeightOn,
  minimumHeight,
  children,
  id,
}) => {
  const collapseBoxRef = React.useRef<HTMLDivElement>(null);
  const height = useChildHeight(collapseBoxRef, isOpen, recalculateHeightOn);
  const boxHeight = Math.max(minimumHeight ?? 0, height);

  return (
    <Body
      id={id}
      isOpen={isOpen}
      minimumHeight={minimumHeight ?? 0}
      ref={collapseBoxRef}
      boxHeight={boxHeight}
      children={isOpen || !isNil(minimumHeight) ? children : []}
    />
  );
};
